import React from 'react'
import {VoiceItem} from '../type'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useI18n} from '../../../../Utils/_helpers/useI18n'

interface VoiceRecordMobileCardProps {
  record: VoiceItem
  formatRecordDate: (value: string) => string
  renderStatusBadge: (status: string) => JSX.Element
  renderSentimentBadge: (sentiment: string) => JSX.Element
  renderTruncatedText: (
    text: string,
    type: 'transcript' | 'summary' | 'welcomeMessage',
    record?: VoiceItem
  ) => JSX.Element
  renderListWithSeeMore: (items: string[], onShowMore: (items: string[]) => void) => JSX.Element
  handleOpenEditModal: (record: VoiceItem) => void
  handleShowOpeners: (items: string[], record: VoiceItem) => void
  handleShowActionItems: (items: string[]) => void
  handleOpenDeletePopup: (record: VoiceItem) => void
  handleDownloadTranscript: (record: VoiceItem) => void
  handleSendToInbox: () => void
  deletingId: string | null
  contactPhone?: string
}

const VoiceRecordMobileCard: React.FC<VoiceRecordMobileCardProps> = ({
  record,
  formatRecordDate,
  renderStatusBadge,
  renderSentimentBadge,
  renderTruncatedText,
  renderListWithSeeMore,
  handleOpenEditModal,
  handleShowOpeners,
  handleShowActionItems,
  handleOpenDeletePopup,
  handleDownloadTranscript,
  handleSendToInbox,
  deletingId,
  contactPhone,
}) => {
  const {t} = useI18n()

  return (
    <div className='voice-record-mobile-card'>
      <div className='card-header-mobile'>
        <div className='record-date-mobile'>
          <i className='bi bi-calendar3 me-2'></i>
          <span>{formatRecordDate(record.createdAt)}</span>
        </div>
        <div className='record-badges-mobile'>
          {renderStatusBadge(record.status)}
          {renderSentimentBadge(record.sentiment)}
        </div>
      </div>

      <div className='card-body-mobile'>
        <div className='detail-item-mobile'>
          <span className='detail-label-mobile'>
            <i className='bi bi-headphones me-1'></i>
            Audio
          </span>
          <div className='detail-value-mobile'>
            <audio controls src={record.voiceFile} style={{width: '100%'}}>
              Your browser does not support the audio element.
            </audio>
          </div>
        </div>

        {record.transcript && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-file-text me-1'></i>
              Transcript
            </span>
            <div className='detail-value-mobile'>
              {renderTruncatedText(record.transcript, 'transcript')}
            </div>
          </div>
        )}

        {record.summary && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-file-earmark-text me-1'></i>
              Summary
            </span>
            <div className='detail-value-mobile'>
              {renderTruncatedText(record.summary, 'summary')}
            </div>
          </div>
        )}

        {record.leadIntent && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-bullseye me-1'></i>
              Lead Intent
            </span>
            <span className='detail-value-mobile fw-bold'>{record.leadIntent}</span>
          </div>
        )}

        {record.welcomeMessage && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-chat-dots me-1'></i>
              Welcome Message
            </span>
            <div className='detail-value-mobile'>
              {renderTruncatedText(record.welcomeMessage, 'welcomeMessage', record)}
            </div>
          </div>
        )}

        {record.nextCallOpeners && record.nextCallOpeners.length > 0 && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-list-ul me-1'></i>
              Openers
            </span>
            <div className='detail-value-mobile'>
              {renderListWithSeeMore(record.nextCallOpeners, (items) =>
                handleShowOpeners(items, record)
              )}
            </div>
          </div>
        )}

        {record.suggestedNextSteps && record.suggestedNextSteps.length > 0 && (
          <div className='detail-item-mobile'>
            <span className='detail-label-mobile'>
              <i className='bi bi-check2-square me-1'></i>
              Action Items
            </span>
            <div className='detail-value-mobile'>
              {renderListWithSeeMore(record.suggestedNextSteps, handleShowActionItems)}
            </div>
          </div>
        )}
      </div>

      <div className='card-footer-mobile'>
        <div className='action-buttons-mobile d-flex gap-2'>
          <button
            className='btn btn-sm btn-icon btn-light btn-active-light-primary'
            title='Edit Welcome Message & Openers'
            onClick={() => handleOpenEditModal(record)}
          >
            <i className='bi bi-pencil'></i>
          </button>
          <button
            className='btn btn-sm btn-icon btn-light btn-active-light-primary'
            title='Send to Inbox'
            onClick={handleSendToInbox}
            disabled={!contactPhone}
          >
            <i className='bi bi-send'></i>
          </button>
          {record.transcript && (
            <button
              className='btn btn-sm btn-icon btn-light btn-active-light-primary'
              title='Download Transcript'
              onClick={() => handleDownloadTranscript(record)}
            >
              <i className='bi bi-download'></i>
            </button>
          )}
          <button
            className={`btn btn-sm btn-icon btn-light btn-active-light-primary ${
              deletingId === record.id ? 'disabled' : ''
            }`}
            onClick={() => deletingId !== record.id && handleOpenDeletePopup(record)}
            disabled={deletingId === record.id}
          >
            <KTSVG className='svg-icon-2' path='/media/icons/duotune/art/trush.svg' />
          </button>
        </div>
      </div>
    </div>
  )
}

export default VoiceRecordMobileCard

